export interface ApiGetRequest {
  apiUrl: string;
  token: string;
  // Allow string | number | undefined — empty string is valid (e.g. status=)
  params?: Record<string, string | number | undefined>;
}

const appendParams = (url: URL, params?: Record<string, string | number | undefined>) => {
  if (!params) return;
  Object.entries(params).forEach(([key, value]) => {
    // Only skip truly undefined/null — empty string '' is intentional
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });
};

export const apiGetRequest = async <RES>(request: ApiGetRequest): Promise<RES> => {
  const url = new URL(request.apiUrl);
  appendParams(url, request.params);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      Authorization: `Bearer ${request.token}`,
    },
  });

  if (response.status === 401) {
    return Promise.reject({ statusCode: 401, message: response.statusText });
  }
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Something went wrong. Please try again.' }));
    return Promise.reject(error);
  }
  return response.json() as Promise<RES>;
};

// Download helper — returns a Blob for file downloads
export const apiGetBlob = async (
  request: ApiGetRequest
): Promise<{ blob: Blob; filename: string }> => {
  const url = new URL(request.apiUrl);
  appendParams(url, request.params);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${request.token}`,
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Download failed. Please try again.' }));
    return Promise.reject(error);
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'report';
  const blob = await response.blob();
  return { blob, filename };
};
